import { useEffect, useRef, useState } from 'react'

/** Where "probably" turns into "likely". Crossing it is the moment worth marking. */
export const CELEBRATION_THRESHOLD = 0.75

const DURATION_MS = 1400

/**
 * Returns true for a moment each time `value` crosses `threshold` upward.
 *
 * Three things this has to get right, all of which are about restraint:
 *
 *  - **Crossing, not exceeding.** Firing whenever the value merely sits above
 *    the threshold would re-trigger on every frame of a drag, which is how a
 *    delight moment becomes a strobe.
 *  - **Never on arrival.** Landing on a shared link that already reads 80% must
 *    not celebrate: the moment belongs to the person who moved a slider and
 *    earned it, not to someone opening a URL.
 *  - **Downward is silent.** Dropping back below the line is not an event. The
 *    fan already shows it, and punctuating a worse outcome would be unkind about
 *    something people are genuinely anxious about.
 *
 * Honours `prefers-reduced-motion` by never firing at all — the CSS-level
 * override in app.css would flatten the animation to nothing anyway, and a
 * component rendering an invisible celebration layer is just wasted work.
 */
export function useThresholdCelebration(
  value: number,
  threshold: number = CELEBRATION_THRESHOLD,
  /**
   * What the value is *about*. Changing it re-baselines instead of comparing.
   *
   * Without this the hook cannot tell "you dragged your odds from 70% to 93%"
   * apart from "you tapped over to the other scenario, which was already at
   * 93%" — both are an upward crossing of the watched number. The second is not
   * an achievement, and celebrating it would spend the one delight moment this
   * screen has on a tab press.
   */
  identity: string = '',
): boolean {
  const previousRef = useRef<number | null>(null)
  const identityRef = useRef(identity)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [celebrating, setCelebrating] = useState(false)

  useEffect(() => {
    const previous = previousRef.current
    previousRef.current = value

    // A different subject: this value has no predecessor, so there is nothing to
    // have crossed. Establish a fresh baseline and wait for the next change.
    if (identityRef.current !== identity) {
      identityRef.current = identity
      return
    }

    // First observed value establishes the baseline; it is not a crossing.
    if (previous === null) return
    if (!(previous < threshold && value >= threshold)) return

    const reduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) return

    setCelebrating(true)
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCelebrating(false), DURATION_MS)
  }, [value, threshold, identity])

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    },
    [],
  )

  return celebrating
}
